/**
 * Automation Runs Service
 * 
 * Provides real-time Firestore subscriptions and queries for automation executions.
 * Powers the Lead Monitor UI with live execution state.
 */
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    doc,
    getDoc,
    getDocs,
    limit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from './firebase.config';

/**
 * Subscribe to automation runs for a specific automation
 * @param {string} automationId - The automation to monitor
 * @param {Function} callback - Called with array of runs on each update
 * @param {Object} options - Optional filters { status, triggeredBy, limit }
 * @returns {Function} Unsubscribe function
 */
export function subscribeToAutomationRuns(automationId, callback, options = {}) {
    if (!automationId) {
        console.warn('[automationRuns.service] No automationId provided');
        callback([]);
        return () => { };
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
        console.warn('[automationRuns.service] No authenticated user');
        callback([]);
        return () => { };
    }

    const runsRef = collection(db, 'automation_executions');

    // Build query constraints - MUST include ownerId for Firestore security rules
    const constraints = [
        where('automationId', '==', automationId),
        where('ownerId', '==', currentUser.uid), // Required for security rules
        orderBy('updatedAt', 'desc'),
    ];

    // Optional status filter
    if (options.status) {
        constraints.push(where('status', '==', options.status));
    }

    // Optional trigger.type (source) filter - 'FORM_SUBMIT' or 'MANUAL_ENTRY'
    if (options.triggerType) {
        constraints.push(where('trigger.type', '==', options.triggerType));
    }

    // Optional limit
    if (options.limit) {
        constraints.push(limit(options.limit));
    }

    const q = query(runsRef, ...constraints);

    return onSnapshot(q,
        (snapshot) => {
            const runs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                // Convert Firestore timestamps to JS dates for display
                startedAt: doc.data().startedAt?.toDate?.() || null,
                updatedAt: doc.data().updatedAt?.toDate?.() || null,
                completedAt: doc.data().completedAt?.toDate?.() || null,
                pausedAt: doc.data().pausedAt?.toDate?.() || null,
                createdAt: doc.data().createdAt?.toDate?.() || null,
            }));
            callback(runs);
        },
        (error) => {
            console.error('[automationRuns.service] Subscription error:', error);
            callback([]);
        }
    );
}

/**
 * Subscribe to all runs for the current user (across all automations)
 * @param {Function} callback - Called with array of runs on each update
 * @param {Object} options - Optional filters { status, limit }
 * @returns {Function} Unsubscribe function
 */
export function subscribeToUserRuns(callback, options = {}) {
    const currentUser = auth.currentUser;
    if (!currentUser) {
        console.warn('[automationRuns.service] No authenticated user');
        callback([]);
        return () => { };
    }

    const runsRef = collection(db, 'automation_executions');

    const constraints = [
        where('ownerId', '==', currentUser.uid),
        orderBy('updatedAt', 'desc'),
    ];

    if (options.status) {
        constraints.push(where('status', '==', options.status));
    }

    if (options.limit) {
        constraints.push(limit(options.limit));
    }

    const q = query(runsRef, ...constraints);

    return onSnapshot(q,
        (snapshot) => {
            const runs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                startedAt: doc.data().startedAt?.toDate?.() || null,
                updatedAt: doc.data().updatedAt?.toDate?.() || null,
                completedAt: doc.data().completedAt?.toDate?.() || null,
                pausedAt: doc.data().pausedAt?.toDate?.() || null,
                createdAt: doc.data().createdAt?.toDate?.() || null,
            }));
            callback(runs);
        },
        (error) => {
            console.error('[automationRuns.service] User runs subscription error:', error);
            callback([]);
        }
    );
}

/**
 * Get runs for a specific lead
 * @param {string} leadId - The lead to query runs for
 * @returns {Promise<Array>} Array of runs
 */
export async function getRunsForLead(leadId) {
    if (!leadId) return [];

    const currentUser = auth.currentUser;
    if (!currentUser) {
        console.warn('[automationRuns.service] No authenticated user');
        return [];
    }

    const runsRef = collection(db, 'automation_executions');
    const q = query(
        runsRef,
        where('leadId', '==', leadId),
        where('ownerId', '==', currentUser.uid), // Required for security rules
        orderBy('updatedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
    }));
}

/**
 * Get a single run by ID
 * @param {string} runId - The execution ID
 * @returns {Promise<Object|null>} Run document or null
 */
export async function getRunById(runId) {
    if (!runId) return null;

    const runRef = doc(db, 'automation_executions', runId);
    const snapshot = await getDoc(runRef);

    if (!snapshot.exists()) return null;

    return {
        id: snapshot.id,
        ...snapshot.data(),
    };
}

/**
 * Pause a running or waiting execution
 * @param {string} runId - The execution ID to pause
 * @returns {Promise<Object>} Result of the pause operation
 */
export async function pauseRun(runId) {
    if (!runId) throw new Error('runId is required');

    const pauseExecution = httpsCallable(functions, 'pauseAutomationExecution');
    const result = await pauseExecution({ executionId: runId });
    return result.data;
}

/**
 * Resume a paused execution
 * @param {string} runId - The execution ID to resume
 * @returns {Promise<Object>} Result of the resume operation
 */
export async function resumeRun(runId) {
    if (!runId) throw new Error('runId is required');

    const resumeExecution = httpsCallable(functions, 'resumeAutomationExecution');
    const result = await resumeExecution({ executionId: runId });
    return result.data;
}

/**
 * Stop (reject) a running execution
 * @param {string} runId - The execution ID to stop
 * @param {string} reason - Optional reason for stopping
 * @returns {Promise<Object>} Result of the stop operation
 */
export async function stopRun(runId, reason = 'manual') {
    if (!runId) throw new Error('runId is required');

    const stopExecution = httpsCallable(functions, 'stopAutomationExecution');
    const result = await stopExecution({ executionId: runId, reason });
    return result.data;
}

/**
 * Delete an execution and all related tasks
 * @param {string} runId - The execution ID to delete
 * @returns {Promise<Object>} Result of the delete operation
 */
export async function deleteRun(runId) {
    if (!runId) throw new Error('runId is required');

    const deleteExecution = httpsCallable(functions, 'deleteAutomationExecution');
    const result = await deleteExecution({ executionId: runId });
    return result.data;
}

/**
 * Get run statistics for an automation
 * @param {string} automationId - The automation ID
 * @returns {Promise<Object>} Stats object { total, running, waiting, paused, completed, failed }
 */
export async function getRunStats(automationId) {
    if (!automationId) return { total: 0, running: 0, waiting: 0, paused: 0, completed: 0, failed: 0 };

    const runsRef = collection(db, 'automation_executions');
    const q = query(runsRef, where('automationId', '==', automationId));

    const snapshot = await getDocs(q);

    const stats = {
        total: 0,
        running: 0,
        waiting: 0,     // NEW: Leads waiting in a wait step
        paused: 0,
        completed: 0,
        failed: 0,
        rejected: 0,
    };

    snapshot.docs.forEach(doc => {
        const status = (doc.data().status || '').toLowerCase();
        stats.total++;
        if (status === 'running') stats.running++;
        else if (status === 'waiting') stats.waiting++;
        else if (status === 'paused') stats.paused++;
        else if (status === 'completed') stats.completed++;
        else if (status === 'failed') stats.failed++;
        else if (status === 'rejected') stats.rejected++;
    });

    return stats;
}

/**
 * Format pause reason for display
 * @param {string} pauseReason - Raw pause reason code
 * @param {Object} pauseDetails - Additional details
 * @returns {string} Human-readable reason
 */
export function formatPauseReason(pauseReason, pauseDetails = {}) {
    if (!pauseReason) return '';

    switch (pauseReason) {
        case 'lead_replied':
            return `Lead Replied${pauseDetails.replyChannel ? ` via ${pauseDetails.replyChannel}` : ''}`;
        case 'status_change':
            return `Status changed to "${pauseDetails.leadStatus || 'unknown'}"`; case 'no_interested_property':
            return 'Lead has no interested property';
        case 'manual':
            return 'Manually stopped';
        default:
            return pauseReason;
    }
}

/**
 * Format wait duration for display
 * @param {Object} config - Wait step config
 * @returns {string} Human-readable duration like "Wait 1 Day"
 */
function formatWaitDuration(config) {
    if (!config) return 'Wait';

    const { waitUnit, waitValue, delaySeconds } = config;

    // Priority 1: Use explicit display values if available
    if (waitUnit && waitValue !== undefined && waitValue !== null) {
        const value = Number(waitValue);
        if (Number.isFinite(value) && value > 0) {
            const unitLabels = {
                days: value === 1 ? 'Day' : 'Days',
                hours: value === 1 ? 'Hour' : 'Hours',
                minutes: value === 1 ? 'Minute' : 'Minutes'
            };
            const label = unitLabels[waitUnit] || waitUnit;
            return `Wait ${value} ${label}`;
        }
    }

    // Priority 2: Derive from delaySeconds (backward compatibility)
    if (delaySeconds && Number.isFinite(Number(delaySeconds))) {
        const seconds = Number(delaySeconds);
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        if (days > 0 && seconds % 86400 === 0) {
            return `Wait ${days} ${days === 1 ? 'Day' : 'Days'}`;
        }
        if (hours > 0 && seconds % 3600 === 0) {
            return `Wait ${hours} ${hours === 1 ? 'Hour' : 'Hours'}`;
        }
        if (minutes > 0) {
            return `Wait ${minutes} ${minutes === 1 ? 'Minute' : 'Minutes'}`;
        }
    }

    return 'Wait';
}

/**
 * Get step title from automation snapshot
 * Computes human-readable titles for wait steps
 * @param {Object} run - The run document
 * @returns {string} Current step title
 */
export function getCurrentStepTitle(run) {
    if (!run) return 'Unknown';

    const steps = run.automationSnapshot?.steps || [];
    const currentIndex = run.currentStepIndex || 0;

    // For completed executions, show "Complete" instead of step info
    // Also handle case where currentIndex is beyond valid steps
    if (run.status === 'completed' || currentIndex >= steps.length) {
        return 'Complete';
    }

    const currentStep = steps[currentIndex];

    if (!currentStep) return 'Unknown';

    // For wait steps, compute a dynamic title from config
    if (currentStep.type === 'wait' && currentStep.config) {
        return formatWaitDuration(currentStep.config);
    }

    return currentStep.title || currentStep.name || `${currentStep.type} Step`;
}

/**
 * Subscribe to ALL active/paused/waiting executions for the current user.
 * Returns a Map<leadId, Array<execution>> grouped by lead for O(1) lookups.
 *
 * Uses a SINGLE Firestore query: ownerId == uid AND status in [running, waiting, paused]
 * Requires composite index: ownerId ASC + status ASC
 *
 * @param {Function} callback - Called with Map<leadId, execution[]> on each update
 * @returns {Function} Unsubscribe function
 */
export function subscribeToActiveExecutions(callback) {
    const currentUser = auth.currentUser;
    if (!currentUser) {
        console.warn('[automationRuns.service] No authenticated user for active executions');
        callback(new Map());
        return () => { };
    }

    const runsRef = collection(db, 'automation_executions');
    const q = query(
        runsRef,
        where('ownerId', '==', currentUser.uid),
        where('status', 'in', ['running', 'waiting', 'paused']),
    );

    return onSnapshot(q,
        (snapshot) => {
            const grouped = new Map();
            snapshot.docs.forEach(docSnap => {
                const data = docSnap.data();
                const leadId = data.leadId;
                if (!leadId) return;

                const execution = {
                    id: docSnap.id,
                    automationId: data.automationId,
                    automationName: data.automationSnapshot?.name || 'Unnamed Automation',
                    status: data.status,   // 'running' | 'waiting' | 'paused'
                    currentStepIndex: data.currentStepIndex ?? 0,
                    totalSteps: data.automationSnapshot?.steps?.length ?? 0,
                    pauseReason: data.pauseReason || null,
                    startedAt: data.startedAt?.toDate?.() || null,
                };

                if (!grouped.has(leadId)) {
                    grouped.set(leadId, []);
                }
                grouped.get(leadId).push(execution);
            });
            callback(grouped);
        },
        (err) => {
            console.error('[automationRuns.service] Active executions listener error:', err);
            callback(new Map());
        }
    );
}

export default {
    subscribeToAutomationRuns,
    subscribeToUserRuns,
    subscribeToActiveExecutions,
    getRunsForLead,
    getRunById,
    pauseRun,
    resumeRun,
    stopRun,
    deleteRun,
    getRunStats,
    formatPauseReason,
    getCurrentStepTitle,
};
